import { Fragment } from 'react';
import { ChatReplyIcon, ChatSeenIcon } from '../../../assets/svg';
import { isPartner } from '../../../helpers/appType';
import { lightTheme } from '../../../styles/theme';
import { ConversationMessageWithStatus } from '../../../types/conversation.types';
import { MediaMeta } from '../chatSurfaces.styled';
import { formatMessageTime } from '../helpers/chatFormat';
import { splitLinks } from '../helpers/chatLinks';
import { MentionCandidate, splitMentions } from '../helpers/chatMentions';
import {
  canTranslate,
  ChatViewer,
  chatMessageDomId,
  isAuthoredBy,
  isDeleted,
  isOwnMessage,
  isViktorMessage,
  messageTone,
  isVisualAttachment,
  OpenUser,
} from '../helpers/chatMessage';
import {
  bodyRepeatsCard,
  bodyWithoutChoices,
  hasMessageCards,
} from '../helpers/chatTriage';
import { useLongPress } from '../hooks/useLongPress';
import { MessageTranslationState } from '../hooks/useMessageTranslations';
import { useSwipeToReply } from '../hooks/useSwipeToReply';
import { MessageActions } from './MessageActions';
import { MessageFooter } from './MessageFooter';
import { MediaAttachments, PlainAttachments } from './MessageAttachments';
import { MessageCards } from './MessageCards';
import { QuickReplies } from './QuickReplies';
import {
  BodyLink,
  Bubble,
  BubbleColumn,
  BubbleGlare,
  BubbleLine,
  BubbleMeta,
  BubbleShell,
  MediaCaption,
  MediaGroup,
  MediaHeader,
  MediaTimeChip,
  Mention,
  MessageRow,
  SwipeReplyHint,
} from './MessageBubble.styled';
import { MessageLead } from './MessageLead';
import { MessageMarkdown } from './MessageMarkdown';
import { MessageTranslationNote } from './MessageTranslationNote';
import { ViktorStatus } from './ViktorStatus';

type Props = {
  message: ConversationMessageWithStatus;
  /** Who is reading, and what kind of conversation this is. */
  viewer: ChatViewer;
  /** Shown when a message has no sender of its own. */
  fallbackSenderName?: string | null;
  /** Whether the viewer may edit/delete their own messages. */
  canWrite?: boolean;
  onReply?: (message: ConversationMessageWithStatus) => void;
  onEdit?: (message: ConversationMessageWithStatus) => void;
  onDelete?: (message: ConversationMessageWithStatus) => void;
  onRetry?: (message: ConversationMessageWithStatus) => void;
  /**
   * Held down on a touch screen. Carries `canModify` because only this
   * component can work it out, and the sheet has to honour the same rule the
   * hover gutter does.
   */
  onLongPress?: (
    message: ConversationMessageWithStatus,
    canModify: boolean
  ) => void;
  /** Deep link for a message pinned to an order. */
  onOpenOrder?: (orderId: number) => void;
  /**
   * Opens a support ticket the AI triage bot raised. Admin only - on the
   * merchant side the card on the message is the whole story.
   */
  onOpenTicket?: (ticketId: number) => void;
  /** Jump to the message this one quotes. */
  onOpenReply?: (messageId: number) => void;
  /** Opens the sender's detail screen. Absent on the merchant app. */
  onOpenUser?: OpenUser;
  /**
   * Everyone in the conversation, so a name written in the body can be told
   * from the text around it. Absent in a support thread, where there is nobody
   * to mention and the body is plain text by definition.
   */
  members?: MentionCandidate[];
  /** A group thread, where `@all` in a body is a mention too. */
  mentionAll?: boolean;
  /** This message's translation, if the reader asked for one. */
  translation?: MessageTranslationState | null;
  /**
   * Translate / See original. Offered on any message with text that somebody
   * else wrote - see `canTranslate` - and never gated on `canWrite`: it
   * changes what the reader sees, nothing about the conversation.
   */
  onTranslate?: (message: ConversationMessageWithStatus) => void;
  /** Plays a one-shot glare, set when another message's reply jumps here. */
  highlighted?: boolean;
  /** This message continues the previous one's run (same side, same author). */
  groupedWithPrevious?: boolean;
  /** The next message continues this one's run. */
  groupedWithNext?: boolean;
};

/**
 * One message.
 *
 * Three shapes, not one. A bubble is for text; media stands on its own and only
 * grows a bubble around it when there is a caption to hold; voice notes and
 * documents are always their own pill. A single message can be more than one of
 * those at once, which is why they stack inside `BubbleShell`.
 */
export const MessageBubble = ({
  message,
  viewer,
  fallbackSenderName,
  canWrite,
  onReply,
  onEdit,
  onDelete,
  onRetry,
  onLongPress,
  onOpenOrder,
  onOpenTicket,
  onOpenReply,
  onOpenUser,
  members,
  mentionAll,
  translation,
  onTranslate,
  highlighted,
  groupedWithPrevious,
  groupedWithNext,
}: Props) => {
  const own = isOwnMessage(message, viewer);
  const deleted = isDeleted(message);
  /*
   * Viktor's answers are the one incoming bubble on a coloured fill - the AI
   * gradient. `strong` is "sits on a coloured surface" and drives every ink
   * choice below; `own` keeps driving position, gutters and footers. In every
   * human thread the two are the same value.
   */
  const viktor = isViktorMessage(message) && !deleted;
  /*
   * A dispatcher thread's note - the one row the customer never sees - sits
   * on our side in white with an amber edge, so it carries dark ink even
   * though it is ours.
   */
  const tone = deleted ? null : messageTone(message);
  const strong = (own && tone !== 'note') || viktor;
  /* Viktor has the question and has not started writing yet. */
  const thinking = !!message.streaming && !message.body;
  /*
   * He has started, and is still at it.
   *
   * Worth its own name because of what this state mostly IS: not text
   * arriving, but the long waits between it. Viktor opens with a sentence
   * saying what he is about to check, then goes quiet for twenty seconds or
   * more while he checks it - and until the next delta lands there is
   * nothing on the bubble to say the question is still being worked on.
   * That is the stretch the shimmer covers.
   */
  const writing = !!message.streaming && !thinking;
  /* An answer still arriving offers nothing yet - no copy of half a sentence,
     and no hold sheet - the same way an unsent row offers nothing. */
  const settled = !deleted && !message.pending && !message.streaming;

  // Optimistic rows have no id yet, so there is nothing to send an edit for.
  const canModify =
    isAuthoredBy(message, viewer) &&
    !deleted &&
    !!canWrite &&
    !message.pending &&
    message.id > 0;

  const translatable = !!onTranslate && canTranslate(message, viewer);
  /* What the bubble shows in place of the body: the translation, while the
     reader has it on. Everything below reads the text from here. */
  const text =
    translation?.status === 'shown' ? translation.text : message.body;

  /* Something to offer at all: the writing actions, or Translate on its own
     for a reader who may not write here. */
  const hasActions = settled && (!!canWrite || translatable);

  /* Nothing to offer on a deleted row or one still in flight - the same test
     the hover gutter uses to render itself at all. */
  const holdHandlers = useLongPress(
    hasActions && onLongPress
      ? () => onLongPress(message, canModify)
      : undefined
  );

  /* The touch screen's fast path to the same reply the hold sheet offers. Same
     gate as the hold: nothing to say to a deleted or unsent message. */
  const swipe = useSwipeToReply(
    settled && canWrite && onReply ? () => onReply(message) : undefined
  );

  const attachments = deleted ? [] : message.attachments;
  const media = attachments.filter(isVisualAttachment);
  const files = attachments.filter((item) => !isVisualAttachment(item));

  /* What the AI triage bot hung on this message: a proposal to confirm, a
     ticket, or both. A deleted row keeps none of it, the same way it keeps no
     attachments. */
  const cards = !deleted && hasMessageCards(message);

  /* Buttons on a question the bot asked, Yes / No or a location each. Not a
     card: the question is plain text, so its buttons go inside the bubble that
     holds it (merchant), and what came of them goes under it (admin). */
  const quickReplies = deleted ? null : message.quickReplies ?? null;

  /* The locations are listed in the question's text as well, for an app that
     cannot draw them. While they are buttons here the list goes, and once the
     buttons are spent it comes back - the question then reads as asked, over
     the answer the merchant tapped. The admin draws no buttons and keeps it. */
  const shownBody =
    isPartner && quickReplies?.active && text
      ? bodyWithoutChoices(text, quickReplies.options)
      : text;

  /* The bot's question is on the card AND in the body - the body is there for
     a client that predates the cards. Here the card is the question, so the
     same sentence in a bubble above it would be the bot asking twice. */
  const hasBody = !!message.body && !(cards && bodyRepeatsCard(message));
  // The quote and the order link read as part of the text, so they pull a media
  // message into the captioned layout too.
  const hasLead = !!message.replyTo || !!message.orderId;
  const framed = hasBody || hasLead;

  /* A card can be the whole message, and then there is nothing for a bubble to
     hold - an empty one with a timestamp floating in it is what the file-pill
     case already avoids. */
  const showTextBubble =
    deleted || (!media.length && (framed || (!files.length && !cards)));

  const metaContent = (iconColor: string) => (
    <>
      {message.editedAt && <span>Edited</span>}
      <span>
        {message.pending ? 'Sending...' : formatMessageTime(message.createdAt)}
      </span>
      {/* Never in a Viktor thread: a read receipt is a person telling you
          they have looked at your message, and Viktor is not one - the backend
          leaves `seen` false on every question there, and a tick that could
          never turn double is a promise the thread cannot keep. */}
      {/* Nor on a note: the customer never gets one, so there is nobody
          whose reading a tick could report. */}
      {own && !message.pending && viewer.type !== 'viktor' && !tone && (
        <ChatSeenIcon double={message.seen} color={iconColor} />
      )}
    </>
  );

  /**
   * Floated inside a bubble or a caption - see BubbleMeta.
   *
   * Ticks only ever appear on your own message, which is a coloured bubble, so
   * they are white rather than the red-when-seen used on the white pills below:
   * red on red is nothing at all. Seen still reads as the tick brightening, and
   * the second tick appearing carries it regardless of colour.
   */
  const inlineMeta = (
    <BubbleMeta $own={strong}>
      {metaContent(
        strong
          ? message.seen
            ? lightTheme.white
            : `${lightTheme.white}80`
          : message.seen
          ? lightTheme.primary
          : lightTheme.lightGray
      )}
    </BubbleMeta>
  );

  const lead = (
    <MessageLead
      message={message}
      own={strong}
      onOpenOrder={onOpenOrder}
      onOpenReply={onOpenReply}
    />
  );

  /* Built once: a message with a picture and a caption renders its text in the
     caption, and a message without one renders it in the bubble - never both,
     but the two sites would otherwise each have to remember the mentions. */
  const body = (
    <MessageBody
      body={shownBody}
      members={members}
      mentionAll={mentionAll}
      own={strong}
      viewerId={viewer.id}
      onOpenUser={onOpenUser}
    />
  );

  return (
    <MessageRow
      $own={own}
      $grouped={groupedWithPrevious}
      id={chatMessageDomId(message.id)}
      {...swipe.rowHandlers}
    >
      <BubbleColumn $own={own} ref={swipe.trackRef}>
        <BubbleLine>
          {/* Hangs off the bubble and rides along as it is dragged - inside the
              line so it centres on the bubble alone, not on the name under it.
              aria-hidden because the swipe is a shortcut, not the only way -
              the hold sheet remains. */}
          <SwipeReplyHint ref={swipe.hintRef} aria-hidden>
            <ChatReplyIcon color={lightTheme.darkGray} />
          </SwipeReplyHint>
          <BubbleShell $own={own} {...holdHandlers}>
            {hasActions && (
              <MessageActions
                message={message}
                own={own}
                canModify={canModify}
                onReply={canWrite ? onReply : undefined}
                onEdit={onEdit}
                onDelete={onDelete}
                translation={translation}
                onTranslate={translatable ? onTranslate : undefined}
              />
            )}

            {/* On the shell rather than on one shape, so a reply jump glares
                whatever the target turned out to be. */}
            {highlighted && <BubbleGlare $own={strong} />}

            {showTextBubble && (
              <Bubble
                $own={strong && !viktor}
                $viktor={viktor}
                $tone={tone}
                $deleted={deleted}
                /* The glow, for the whole run - thinking dots included: unlike
                   the sweep it needs no room to read, and a glowing pill is
                   exactly what the dots want to sit in. */
                $working={!!message.streaming}
              >
                {/* Inside the bubble rather than over the shell, unlike the
                    jump glare above: here the sweep belongs to the words, so
                    it has to be clipped to their own rounded corners and to
                    pass across them rather than across whatever else the
                    message is carrying. */}
                {writing && <BubbleGlare $own={strong} $streaming />}
                {deleted ? (
                  'Message deleted'
                ) : (
                  <>
                    {lead}
                    {thinking ? (
                      /* No meta beside it: there is nothing to timestamp yet,
                         and a clock next to three dots reads as a message that
                         arrived empty. */
                      <ViktorStatus standalone />
                    ) : viktor ? (
                      /* Viktor writes Markdown; a person's text stays literal.
                         The meta goes INSIDE the formatted content - see the
                         `trailing` prop for why it cannot follow it. */
                      <>
                        <MessageMarkdown
                          body={text ?? ''}
                          own={strong}
                          /* No clock on an answer still being written: it
                             would time a message that has not happened yet,
                             and it would sit between the words and the line
                             saying what Viktor is doing to them. The dots
                             phase above goes without one for the same
                             reason. */
                          trailing={writing ? undefined : inlineMeta}
                          /* Each block fades in as it lands, and only while
                             the answer is still arriving - the stored copy
                             that replaces this carries the same words. */
                          animate={writing}
                        />
                        {/* Under the half-written answer: he wrote a line
                            saying what he was about to check, and this says
                            he is still checking it. */}
                        {writing && <ViktorStatus />}
                      </>
                    ) : (
                      <>
                        {body}
                        {quickReplies && isPartner && (
                          <QuickReplies
                            messageId={message.id}
                            quickReplies={quickReplies}
                            interactive
                          />
                        )}
                        {inlineMeta}
                      </>
                    )}
                  </>
                )}
              </Bubble>
            )}

            {quickReplies && !isPartner && (
              <QuickReplies
                messageId={message.id}
                quickReplies={quickReplies}
                interactive={false}
              />
            )}

            {media.length > 0 && (
              <MediaGroup $own={own} $framed={framed}>
                {hasLead && <MediaHeader>{lead}</MediaHeader>}

                <MediaAttachments
                  attachments={media}
                  flush={framed}
                  /* The picture is the local file until the server answers, so
                     it needs to say so itself - "Sending..." in the meta line
                     is the wrong size of signal next to a full photograph. */
                  uploading={message.pending}
                  meta={
                    hasBody ? null : (
                      // Nothing else can carry the time, so it goes on the
                      // picture. Videos take the opposite corner - the bottom
                      // edge belongs to their scrubber.
                      <MediaTimeChip
                        $topLeft={media[media.length - 1].type === 'video'}
                      >
                        {/* White in both states: the chip already reads as
                            "yours" by sitting on your own picture, and the red
                            tick is lost against the scrim. Seen still shows as
                            the second tick appearing. */}
                        {metaContent(lightTheme.white)}
                      </MediaTimeChip>
                    )
                  }
                />

                {hasBody && (
                  <MediaCaption>
                    {body}
                    {inlineMeta}
                  </MediaCaption>
                )}
              </MediaGroup>
            )}

            {files.length > 0 && (
              <PlainAttachments
                attachments={files}
                meta={
                  // Only when the pill is the whole message; otherwise the
                  // bubble or the caption above it already showed the time.
                  showTextBubble || media.length > 0 ? null : (
                    <MediaMeta>
                      {metaContent(
                        message.seen ? lightTheme.primary : lightTheme.lightGray
                      )}
                    </MediaMeta>
                  )
                }
              />
            )}

            {cards && (
              <MessageCards
                message={message}
                onOpenTicket={onOpenTicket}
                onOpenOrder={onOpenOrder}
                meta={
                  // Same rule as the file pill above: the card carries the time
                  // only when nothing before it could.
                  showTextBubble ||
                  media.length > 0 ||
                  files.length > 0 ? null : (
                    <MediaMeta>
                      {metaContent(
                        message.seen ? lightTheme.primary : lightTheme.lightGray
                      )}
                    </MediaMeta>
                  )
                }
              />
            )}
          </BubbleShell>
        </BubbleLine>

        {translatable && (
          <MessageTranslationNote
            translation={translation ?? null}
            own={own}
            onToggle={() => onTranslate?.(message)}
          />
        )}

        <MessageFooter
          message={message}
          viewer={viewer}
          own={own}
          fallbackSenderName={fallbackSenderName}
          /* The name belongs to the run, not to every line: it sits on the last
             message of it. */
          endsRun={!groupedWithNext}
          onRetry={onRetry}
          onOpenUser={onOpenUser}
        />
      </BubbleColumn>
    </MessageRow>
  );
};

/**
 * The message text, with the names and the links in it picked out.
 *
 * Split at render time rather than stored: the body is a plain string, and the
 * ids that came with it say who to push, not where in the sentence they are
 * written. Mentions are cut out first and links found in what remains - in that
 * order, so a URL can never claim half of somebody's handle.
 * See `helpers/chatMentions` and `helpers/chatLinks`.
 */
const MessageBody = ({
  body,
  members,
  mentionAll,
  own,
  viewerId,
  onOpenUser,
}: {
  body: string | null;
  members?: MentionCandidate[];
  mentionAll?: boolean;
  own: boolean;
  viewerId: number | null;
  onOpenUser?: OpenUser;
}) => (
  <>
    {splitMentions(body ?? '', members, mentionAll).map((run, index) => {
      // Keyed by position: this is a pure derivation of one string, so a run
      // has no identity beyond where it sits in it.
      if (run.all) {
        /* Everyone, so nobody to open - and the reader is among them, so it is
           marked the way their own name would be. The `mentionUserIds` behind
           it list the whole group; drawing those would print every name. */
        return (
          <Mention key={index} $own={own} $me>
            {run.text}
          </Mention>
        );
      }

      if (run.userId === undefined) {
        return (
          <Fragment key={index}>
            {splitLinks(run.text).map((piece, pieceIndex) =>
              piece.href ? (
                <BodyLink
                  key={pieceIndex}
                  href={piece.href}
                  $own={own}
                  /* A new tab, like every outbound step in this app - and the
                     opener severed, because the destination is whatever
                     somebody pasted. */
                  target="_blank"
                  rel="noopener noreferrer"
                  /* The bubble around it has its own ideas about a tap - a
                     caption's link must not also open the lightbox. */
                  onClick={(event) => event.stopPropagation()}
                >
                  {piece.text}
                </BodyLink>
              ) : (
                <Fragment key={pieceIndex}>{piece.text}</Fragment>
              )
            )}
          </Fragment>
        );
      }

      /*
       * A mention is a person, so it opens them - the same screen, in the same
       * new tab, as their name under a bubble. The member is looked up rather
       * than carried on the run because the route depends on the role: sales
       * lives on a page of its own.
       *
       * Nothing to open on the merchant app, which passes no handler and has
       * no such screen; there the name is coloured text and nothing more.
       */
      const member = members?.find((candidate) => candidate.id === run.userId);
      const open =
        onOpenUser && member
          ? () => onOpenUser(member.id, member.role ?? null)
          : undefined;

      return (
        <Mention
          key={index}
          $own={own}
          $me={run.userId === viewerId}
          $clickable={!!open}
          onClick={open}
        >
          {run.text}
        </Mention>
      );
    })}
  </>
);
